import { useCallback, useMemo, useRef, useState } from 'react';
import debounce from 'react-utils/debounce';

import { useHttpClient } from '../../client/HttpClientContext';
import {
  incrementAutosaveClearPendingRequestsMetric,
  incrementAutosaveRequestOnSaveMetric,
} from '../../metrics/autosaveMetrics';

import { useItemRegistry } from './useItemRegistry';
import { useAutosaveWindowEvents } from './useAutosaveWindowEvents';
import { useOnObjectsUpdated } from './useOnObjectsUpdated';
import { AutosaveStatus, useAutosaveStatus } from './useAutosaveStatus';
import { savePropertyItems, undoPropertyItems } from '../property';
import {
  AutosaveItem,
  AutosaveOperation,
  AutosaveRequest,
  RequestHandler,
} from '../types';

export interface UseAutosaveCoreResult {
  cancelRequestsForSource: (sourceId: string) => void;
  cancelRequestsForForm: (formId: string) => void;
  clearPendingRequests: () => void;
  enqueueRequest: (args: AutosaveOperation) => void;
  getRegisteredFieldCount: () => number;
  hasRequestForSource: (sourceId: string) => boolean;
  lastSavedItems: Array<AutosaveItem>;
  registerHandler: (handler: RequestHandler) => () => void;
  requests: AutosaveRequest[];
  invalidRequests: AutosaveRequest[];
  status: AutosaveStatus;
  saveValidRequests: () => void;
  saveValidRequestsImmediate: () => void;
  undo: () => void;
}

export const useAutosaveCore = (): UseAutosaveCoreResult => {
  const httpClient = useHttpClient();
  const onObjectsUpdated = useOnObjectsUpdated();

  const { status, statusHandlers } = useAutosaveStatus();
  const handlersRef = useRef<Map<string, RequestHandler>>(new Map());
  const [lastSavedItems, setLastSavedItems] = useState<Array<AutosaveItem>>([]);

  const itemRegistry = useItemRegistry<AutosaveOperation>();

  const inFlightSaveCount = useRef<number>(0);
  const getHasInFlightSaves = useCallback(
    () => inFlightSaveCount.current > 0,
    [inFlightSaveCount]
  );
  useAutosaveWindowEvents(getHasInFlightSaves);

  const undo = useCallback(() => {
    statusHandlers.undoStarted();
    void undoPropertyItems(lastSavedItems, httpClient, onObjectsUpdated);
  }, [statusHandlers, lastSavedItems, httpClient, onObjectsUpdated]);

  const save = useCallback(
    (items: AutosaveItem[]) => {
      inFlightSaveCount.current++;
      statusHandlers.savingStarted();

      savePropertyItems(items, httpClient, onObjectsUpdated)
        .then((allSuccess) => {
          if (allSuccess) {
            statusHandlers.savingSucceeded();
          } else {
            statusHandlers.savingFailed();
          }
        })
        .catch(() => {})
        .finally(() => {
          setLastSavedItems(items);
          inFlightSaveCount.current--;
        });
    },
    [statusHandlers, httpClient, onObjectsUpdated]
  );

  const saveValidRequests = useCallback(() => {
    const handlers = Array.from(handlersRef.current.values());
    const itemsToProcess = itemRegistry
      .getAll()
      .filter((item) =>
        handlers.every(
          ({ getIsHandlerForRequest, getIsValidRequest }) =>
            !getIsHandlerForRequest(item.data.request) ||
            getIsValidRequest(item.data.request)
        )
      );

    for (const item of itemRegistry.getAll()) {
      incrementAutosaveRequestOnSaveMetric({
        isSaved: itemsToProcess.includes(item),
        isFormRequest: !!item.data.request.formMetadata,
      });
    }

    if (itemsToProcess.length > 0) {
      itemRegistry.filter((item) => !itemsToProcess.includes(item));
      save(itemsToProcess);
    }
  }, [save, itemRegistry]);

  const cancelRequestsForForm = useCallback(
    (formId: string) => {
      for (const item of itemRegistry.getAll()) {
        if (item.data.request.formMetadata?.formId === formId) {
          itemRegistry.remove(item.id);
          item.data.onClear();
        }
      }
    },
    [itemRegistry]
  );

  const cancelRequestsForSource = useCallback(
    (sourceId: string) => {
      if (itemRegistry.hasItem(sourceId)) {
        itemRegistry.remove(sourceId);
      }
    },
    [itemRegistry]
  );

  const clearPendingRequests = useCallback(() => {
    const items = itemRegistry.getAll();
    itemRegistry.clear();
    for (const item of items) {
      item.data.onClear();
    }
    statusHandlers.requestsCleared();
    incrementAutosaveClearPendingRequestsMetric();
  }, [itemRegistry, statusHandlers]);

  const enqueueRequest = useCallback(
    (operation: AutosaveOperation) => {
      itemRegistry.add({
        id: operation.request.sourceId,
        data: operation,
        timestamp: Date.now(),
      });
    },
    [itemRegistry]
  );

  const registerHandler = useCallback((config: RequestHandler) => {
    handlersRef.current.set(config.id, config);
    return () => {
      handlersRef.current.delete(config.id);
    };
  }, []);

  const getRegisteredFieldCount = useCallback(
    () => handlersRef.current.size,
    []
  );

  const items = itemRegistry.getAll();
  const requests = useMemo(
    () => items.map((item) => item.data.request),
    [items]
  );

  const invalidRequests = useMemo(() => {
    // eslint-disable-next-line react-hooks/refs
    const handlers = Array.from(handlersRef.current.values());
    return items
      .filter((item) =>
        handlers.some(
          ({ getIsHandlerForRequest, getIsValidRequest }) =>
            getIsHandlerForRequest(item.data.request) &&
            !getIsValidRequest(item.data.request)
        )
      )
      .map((item) => item.data.request);
  }, [items]);

  return {
    cancelRequestsForSource,
    cancelRequestsForForm,
    clearPendingRequests,
    enqueueRequest,
    getRegisteredFieldCount,
    hasRequestForSource: itemRegistry.hasItem,
    invalidRequests,
    lastSavedItems,
    registerHandler,
    requests,
    saveValidRequests: useMemo(
      // eslint-disable-next-line react-hooks/refs
      () => debounce(saveValidRequests, 25),
      [saveValidRequests]
    ),
    saveValidRequestsImmediate: saveValidRequests,
    status,
    undo,
  };
};
